import { MediaCrop, MediaItem, MediaKind } from './media-api';
import { layerOf, Region, Size } from './media-frame';
import { canRetryError, splitItemError } from './media-errors';

/** What an upload that this browser is doing looks like to the grid (the uploader keeps the file and the address; the grid only needs this). */
export interface LocalUpload {
  /** A number of this page: the tile of an upload keeps its key from the first byte to the end. */
  uid: number;
  /** The id of the item once `POST /uploads` has made it. */
  itemId: number | null;
  fileName: string;
  kind: MediaKind;
  size: number;
  /** The bytes that have gone out. */
  sent: number;
  /** `queued` waits for the ones before it; `starting` asks the server; `sending` puts the file; `finishing` tells the server it is all there; `failed` stopped. */
  stage: 'queued' | 'starting' | 'sending' | 'finishing' | 'failed';
  /** The item this file replaces, if it does. Such an upload has no tile of its own: it shows on the tile it replaces. */
  replaceId: number | null;
  /** A picture of the chosen file for the tile (an object URL; empty for a format the browser cannot draw). */
  preview: string | null;
  /** The text of why a `failed` upload stopped. */
  failure?: string;
}

export type TileState = 'queued' | 'uploading' | 'interrupted' | 'processing' | 'delayed' | 'ready' | 'failed';

/** Where the picture of a tile comes from. */
export type TileThumb =
  /** Public files of the item: `src` of 1x / 2x. */
  | { type: 'public'; src: string; srcset: string | null }
  /** A private preview of the job (fetched with the login), optionally cut to the frame. */
  | { type: 'private'; url: string; layer: { width: string; left: string; top: string } | null }
  /** The picked file itself. */
  | { type: 'local'; url: string }
  | { type: 'none' };

/** The new file of an item that is being replaced. */
export interface ReplacementView {
  state: 'uploading' | 'processing' | 'delayed' | 'failed';
  percent: number | null;
  id: number | null;
  uid: number | null;
  errorKey: string | null;
  /** «Повторить» can help (a failure that is not the file's own fault). */
  canRetry: boolean;
}

export interface Tile {
  /** The key of the tile in the grid: stable from the first byte of an upload to its end. */
  key: string;
  id: number | null;
  uid: number | null;
  kind: MediaKind;
  state: TileState;
  /** 0 for the main. */
  index: number;
  main: boolean;
  /** Uploading: 0..100. */
  percent: number | null;
  sent: number | null;
  size: number | null;
  fileName: string | null;
  thumb: TileThumb;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  /** The widest file of a published item. */
  maxWidth: number | null;
  warnLowRes: boolean;
  /** The job has read the original: the frame can be set. */
  probed: boolean;
  canFrame: boolean;
  errorKey: string | null;
  /** The tail of the tool's message (for a tooltip). */
  errorTail: string;
  /** A failed item: «Повторить» is offered (not for the errors of the file itself: too small, too long, not a picture). */
  canRetry: boolean;
  replacement: ReplacementView | null;
  /** The upload of this tile ended with an error of this page's own (the file stopped on its way). */
  uploadFailure: string | null;
  /** The item is there but nobody in this browser is sending it. */
  hasFile: boolean;
}

export interface TileInput {
  items: readonly MediaItem[];
  /** The ids the grid shows, in order (the hidden ones are not here). */
  order: readonly number[];
  /** The items whose deletion waits for its ten seconds: the server still lists them, the grid does not. */
  hidden: ReadonlySet<number>;
  uploads: readonly LocalUpload[];
  /** The pictures of files that have gone up (item id → object URL), for the time the server has no picture of its own yet. */
  thumbs: ReadonlyMap<number, string>;
}

export function widthUrl(template: string, width: number): string {
  return template.replace('{w}', String(width));
}

/** The widest of the widths a published item has. */
export function widestOf(widths: readonly number[] | null | undefined): number | null {
  return widths && widths.length > 0 ? Math.max(...widths) : null;
}

/** The width for a place of this many pixels on a screen of density 1: the smallest that is not smaller, else the biggest there is. */
export function pickWidth(widths: readonly number[], want: number): number | null {
  const sorted = [...widths].sort((a, b) => a - b);
  return sorted.find(w => w >= want) ?? sorted[sorted.length - 1] ?? null;
}

/** `src` and `srcset` (1x, 2x) of a published item for a place of `css` pixels. */
export function publicThumb(template: string, widths: readonly number[], css: number): { src: string; srcset: string | null } | null {
  const one = pickWidth(widths, css);
  if (one === null) {
    return null;
  }
  const two = pickWidth(widths, css * 2);
  return { src: widthUrl(template, one), srcset: two !== null && two !== one ? `${widthUrl(template, one)} 1x, ${widthUrl(template, two)} 2x` : null };
}

export function sizeOf(item: Pick<MediaItem, 'width' | 'height'>): Size | null {
  return item.width != null && item.height != null ? { width: item.width, height: item.height } : null;
}

function regionOf(crop: MediaCrop | null | undefined): Region | null {
  return crop ? { x: crop.x, y: crop.y, w: crop.w, h: crop.h } : null;
}

function thumbOf(item: MediaItem, local: LocalUpload | undefined, kept: string | undefined, css: number): TileThumb {
  if (item.src && item.widths && item.widths.length > 0) {
    const pick = publicThumb(item.src, item.widths, css);
    if (pick) {
      return { type: 'public', ...pick };
    }
  }
  const size = sizeOf(item);
  if (item.previewUrl) {
    const region = regionOf(item.crop);
    return { type: 'private', url: item.previewUrl, layer: size && region ? layerOf(size, region) : null };
  }
  const own = local?.preview ?? kept;
  return own ? { type: 'local', url: own } : { type: 'none' };
}

function percentOf(upload: LocalUpload): number {
  if (upload.stage === 'finishing') {
    return 100;
  }
  return upload.size > 0 ? Math.min(99, Math.floor((upload.sent / upload.size) * 100)) : 0;
}

function replacementOf(target: MediaItem, uploads: readonly LocalUpload[]): ReplacementView | null {
  const local = uploads.find(u => u.replaceId === target.id);
  const server = target.replacement ?? null;
  if (local && local.stage !== 'failed' && !(server && server.status !== 'UPLOADING')) {
    return { state: 'uploading', percent: percentOf(local), id: local.itemId ?? server?.id ?? null, uid: local.uid, errorKey: null, canRetry: true };
  }
  if (local && local.stage === 'failed') {
    return { state: 'failed', percent: null, id: local.itemId ?? server?.id ?? null, uid: local.uid, errorKey: null, canRetry: true };
  }
  if (!server) {
    return null;
  }
  const failed = server.status === 'FAILED';
  const failure = failed ? splitItemError(server.error).key : null;
  return {
    state: server.status === 'UPLOADING' ? 'uploading' : failed ? 'failed' : server.delayed ? 'delayed' : 'processing',
    percent: null,
    id: server.id,
    uid: null,
    errorKey: failure,
    canRetry: canRetryError(failure)
  };
}

function itemTile(item: MediaItem, index: number, uploads: readonly LocalUpload[], thumbs: ReadonlyMap<number, string>): Tile {
  const local = uploads.find(u => u.itemId === item.id && u.replaceId === null);
  let state: TileState;
  if (local && local.stage !== 'failed') {
    state = 'uploading';
  } else if (local && local.stage === 'failed') {
    state = 'failed';
  } else if (item.status === 'UPLOADING') {
    state = 'interrupted';
  } else if (item.status === 'PROCESSING') {
    state = item.delayed ? 'delayed' : 'processing';
  } else if (item.status === 'FAILED') {
    state = 'failed';
  } else {
    state = 'ready';
  }
  const probed = item.width != null && !!item.previewUrl;
  const error = splitItemError(item.error);
  return {
    key: local ? `u${local.uid}` : `m${item.id}`,
    id: item.id,
    uid: local?.uid ?? null,
    kind: item.type,
    state,
    index,
    main: index === 0,
    percent: local ? percentOf(local) : null,
    sent: local ? local.sent : null,
    size: local ? local.size : (item.fileSize ?? null),
    fileName: item.fileName ?? local?.fileName ?? null,
    thumb: thumbOf(item, local, thumbs.get(item.id), 160),
    width: item.width ?? null,
    height: item.height ?? null,
    durationMs: item.durationMs ?? null,
    maxWidth: widestOf(item.widths),
    warnLowRes: item.warnLowRes,
    probed,
    canFrame: probed && state !== 'uploading' && state !== 'interrupted' && state !== 'failed',
    errorKey: state === 'failed' && !local ? error.key || null : null,
    errorTail: state === 'failed' ? error.tail : '',
    canRetry: canRetryError(error.key),
    replacement: replacementOf(item, uploads),
    uploadFailure: local && local.stage === 'failed' ? (local.failure ?? '') : null,
    hasFile: !!local
  };
}

function localTile(upload: LocalUpload, index: number): Tile {
  const failed = upload.stage === 'failed';
  return {
    key: `u${upload.uid}`,
    id: null,
    uid: upload.uid,
    kind: upload.kind,
    state: failed ? 'failed' : upload.stage === 'queued' ? 'queued' : 'uploading',
    index,
    main: index === 0,
    percent: percentOf(upload),
    sent: upload.sent,
    size: upload.size,
    fileName: upload.fileName,
    thumb: upload.preview ? { type: 'local', url: upload.preview } : { type: 'none' },
    width: null,
    height: null,
    durationMs: null,
    maxWidth: null,
    warnLowRes: false,
    probed: false,
    canFrame: false,
    errorKey: null,
    errorTail: '',
    canRetry: true,
    replacement: null,
    uploadFailure: failed ? (upload.failure ?? '') : null,
    hasFile: true
  };
}

/**
 * The tiles of the grid, in their order: the items of the size in `order` (what is hidden is not in it), then the uploads that have no item yet (they wait their turn at
 * the end, as the server will put them). An upload that replaces an item is not a tile: it shows on the tile it replaces.
 */
export function buildTiles(input: TileInput): Tile[] {
  const byId = new Map(input.items.map(i => [i.id, i]));
  const tiles: Tile[] = [];
  for (const id of input.order) {
    const item = byId.get(id);
    if (item) {
      tiles.push(itemTile(item, tiles.length, input.uploads, input.thumbs));
    }
  }
  const shown = new Set(input.order);
  // an item that the order does not know yet (made a moment ago) goes at its server place, after the ones that are known
  for (const item of [...input.items].sort((a, b) => a.position - b.position)) {
    if (!shown.has(item.id) && !input.hidden.has(item.id) && !tiles.some(t => t.id === item.id)) {
      tiles.push(itemTile(item, tiles.length, input.uploads, input.thumbs));
    }
  }
  for (const upload of input.uploads) {
    if (upload.replaceId === null && (upload.itemId === null || !byId.has(upload.itemId))) {
      tiles.push(localTile(upload, tiles.length));
    }
  }
  return tiles;
}

/** The tiles the buyer sees: the items that have files in the public bucket (also while one is cut again: the old files stay), in the order of the grid. */
export function publishedTiles(tiles: readonly Tile[]): Tile[] {
  return tiles.filter(t => t.id !== null && t.maxWidth != null);
}

/** The address of the old photo with the time its list was read, so a copy the browser kept (the picture is cached for an hour) is not shown after the server restores it. */
export function versioned(url: string, stamp: number): string {
  return `${url}${url.includes('?') ? '&' : '?'}v=${stamp}`;
}

/** What the size has, for the counters and the limits: the tiles that are not a replacement. */
export function countTiles(tiles: readonly Tile[]): { photos: number; videos: number } {
  return {
    photos: tiles.filter(t => t.kind === 'PHOTO').length,
    videos: tiles.filter(t => t.kind === 'VIDEO').length
  };
}
